import { useEffect, useRef, useCallback } from 'react';
import { useAppStore } from '../store/useAppStore';
import { postRun, getRun, createEventSource } from '../services/api';
import type { DownloadArtifact, LogEntry, PipelineStage, RunRecord } from '../types';

const inflightRefreshes = new Map<string, Promise<RunRecord | null>>();
let sharedEventSource: EventSource | null = null;
let sharedPollingTimer: ReturnType<typeof setInterval> | null = null;
let sharedClosed = true;
let sharedRunId = '';

function isTerminalStatus(status: string): boolean {
  return status === 'completed' || status === 'failed' || status === 'interrupted' || status === 'cancelled';
}

function isLiveStatus(status: string): boolean {
  return status === 'queued' || status === 'running';
}

function appendLogLine(run: RunRecord | null, entry: LogEntry): RunRecord | null {
  if (!run) return run;
  return { ...run, logs: [...run.logs, entry].slice(-200) };
}

function parseTimestamp(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = new Date(value).getTime();
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

function parseStages(data: Record<string, unknown>): PipelineStage[] {
  return ((data.stages || []) as any[]).map((s: any) => ({
    key: s.key,
    label: s.label,
    status: s.status,
    percent: s.percent || 0,
    message: s.message || '',
    startedAt: s.startedAt || null,
    metrics: s.metrics || {},
  }));
}

function parseDownloads(data: Record<string, unknown>): DownloadArtifact[] {
  return ((data.artifacts || []) as any[]).map((a: any) => ({
    type: a.type,
    label: a.label,
    status: a.status,
    url: a.url,
    mimeType: a.mimeType,
  }));
}

function mergeStageUpdate(stages: PipelineStage[], payload: Record<string, any>): PipelineStage[] {
  const stageKey = payload.stage;
  if (!stageKey) return stages;
  const progress = typeof payload.progress === 'number' ? payload.progress : undefined;
  const existingIndex = stages.findIndex((stage) => stage.key === stageKey);
  if (existingIndex < 0) return stages;
  return stages.map((stage, index) => {
    if (index < existingIndex) return { ...stage, status: 'completed', percent: 100 };
    if (index > existingIndex) return stage.status === 'completed' ? stage : { ...stage, status: 'pending' };
    return {
      ...stage,
      status: payload.status || 'running',
      percent: progress == null ? Math.max(stage.percent || 1, 1) : Math.max(stage.percent || 0, Math.min(99, progress)),
      message: payload.message || stage.message,
      metrics: payload.metrics || stage.metrics,
    };
  });
}

export function buildPlaceholderRun(options: {
  runId: string;
  topic: string;
  status?: string;
  progress?: number;
  currentStage?: string;
  message?: string;
  createdAt?: number;
}): RunRecord {
  return {
    runId: options.runId,
    topic: options.topic,
    status: options.status || 'queued',
    progress: options.progress ?? 0,
    currentStage: options.currentStage || '',
    message: options.message || '',
    stages: [],
    logs: [],
    reviewSections: [],
    citations: null,
    downloads: [],
    errors: [],
    warnings: [],
    phaseTimings: {},
    costSummary: {},
    createdAt: options.createdAt ?? Date.now(),
  };
}

function parseRunResponse(runId: string, data: Record<string, unknown>, existing: RunRecord | null): RunRecord {
  const run = ((data as any).run && typeof (data as any).run === 'object') ? (data as any).run : data;
  const createdAt = parseTimestamp((run as any).startedAt || (run as any).created_at || (run as any).createdAt, existing?.createdAt ?? Date.now());
  const status = String((data as any).state || (run as any).state || (run as any).status || existing?.status || 'queued');

  return {
    runId,
    topic: (run as any).topic || existing?.topic || '',
    status,
    progress: typeof (data as any).overallProgress === 'number'
      ? (data as any).overallProgress
      : typeof (run as any).progress === 'number'
        ? (run as any).progress
        : isTerminalStatus(status) ? 100 : (existing?.progress || 0),
    currentStage: ((data as any).currentStage as string) || (run as any).currentStage || (run as any).current_step || existing?.currentStage || '',
    message: ((data as any).message as string) || (run as any).message || (run as any).error || existing?.message || '',
    stages: parseStages(data),
    logs: existing?.logs || [],
    reviewSections: existing?.reviewSections || [],
    citations: existing?.citations || null,
    downloads: parseDownloads(data),
    errors: (data.errors as any[]) || [],
    warnings: (data.warnings as any[]) || [],
    phaseTimings: (data.phaseTimings as Record<string, number>) || {},
    costSummary: (data.costSummary as Record<string, unknown>) || {},
    createdAt,
  };
}

export function useRunManager() {
  const setCurrentRun = useAppStore((state) => state.setCurrentRun);
  const addToHistory = useAppStore((state) => state.addToHistory);
  const esRef = useRef<EventSource | null>(sharedEventSource);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(sharedPollingTimer);
  const closedRef = useRef(sharedClosed);
  const subscribedRunIdRef = useRef(sharedRunId);

  const syncSharedRefs = useCallback(() => {
    sharedEventSource = esRef.current;
    sharedPollingTimer = pollingRef.current;
    sharedClosed = closedRef.current;
    sharedRunId = subscribedRunIdRef.current;
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    syncSharedRefs();
  }, [syncSharedRefs]);

  const primeRun = useCallback((run: RunRecord) => {
    setCurrentRun(run);
    addToHistory({
      runId: run.runId,
      topic: run.topic,
      status: run.status,
      createdAt: run.createdAt,
      progress: run.progress,
    });
  }, [setCurrentRun, addToHistory]);

  const refreshRun = useCallback((runId: string): Promise<RunRecord | null> => {
    const inflight = inflightRefreshes.get(runId);
    if (inflight) return inflight;

    const request = (async () => {
      try {
        const existing = useAppStore.getState().currentRun?.runId === runId ? useAppStore.getState().currentRun : null;
        const runData = await getRun(runId);
        const record = parseRunResponse(runId, runData, existing);

        setCurrentRun(record);
        addToHistory({
          runId,
          topic: record.topic,
          status: record.status,
          createdAt: record.createdAt,
          progress: record.progress,
        });

        if (isTerminalStatus(record.status)) {
          stopPolling();
        }

        return record;
      } catch {
        return null;
      } finally {
        inflightRefreshes.delete(runId);
      }
    })();

    inflightRefreshes.set(runId, request);
    return request;
  }, [setCurrentRun, addToHistory, stopPolling]);

  const launchRun = useCallback(async (payload: Record<string, unknown>) => {
    const { runId } = await postRun(payload);
    return runId;
  }, []);

  const subscribeSSE = useCallback((runId: string) => {
    if (subscribedRunIdRef.current === runId && esRef.current && !closedRef.current) return;
    if (esRef.current) esRef.current.close();
    stopPolling();
    closedRef.current = false;
    subscribedRunIdRef.current = runId;
    const es = createEventSource(runId);
    esRef.current = es;
    syncSharedRefs();

    pollingRef.current = setInterval(() => {
      const current = useAppStore.getState().currentRun;
      if (current?.runId === runId && isTerminalStatus(current.status)) {
        stopPolling();
        return;
      }
      void refreshRun(runId);
    }, 5000);
    void refreshRun(runId);
    syncSharedRefs();

    const syncTerminalState = (
      status: 'completed' | 'failed' | 'interrupted',
      nextMessage?: string,
      nextErrors?: { code: string; message: string; agent: string }[],
    ) => {
      const currentRecord = useAppStore.getState().currentRun;
      if (currentRecord && currentRecord.runId === runId) {
        const next = {
          ...currentRecord,
          status,
          message: nextMessage || currentRecord.message,
          errors: nextErrors || currentRecord.errors,
        };
        setCurrentRun(next);
        addToHistory({ runId, topic: next.topic, status: next.status, createdAt: next.createdAt, progress: next.progress });
      }
      closedRef.current = true;
      subscribedRunIdRef.current = '';
      es.close();
      if (esRef.current === es) esRef.current = null;
      syncSharedRefs();
      useAppStore.getState().setSseConnected(false);
      stopPolling();
    };

    es.onopen = () => {
      closedRef.current = false;
      syncSharedRefs();
      useAppStore.getState().setSseConnected(true);
    };

    es.addEventListener('task.stage.updated', (event) => {
      try {
        const payload = JSON.parse((event as MessageEvent).data || '{}');
        const currentRecord = useAppStore.getState().currentRun;
        if (!currentRecord || currentRecord.runId !== runId) return;
        setCurrentRun({
          ...currentRecord,
          status: payload.status || currentRecord.status,
          currentStage: payload.stage || currentRecord.currentStage,
          progress: typeof payload.progress === 'number' ? payload.progress : currentRecord.progress,
          message: payload.message || currentRecord.message,
          stages: mergeStageUpdate(currentRecord.stages, payload),
        });
      } catch {
        void refreshRun(runId);
      }
    });

    es.addEventListener('task.log.appended', (event) => {
      try {
        const payload = JSON.parse((event as MessageEvent).data || '{}');
        const entry: LogEntry = {
          id: `${runId}-${payload.timestamp || Date.now()}`,
          timestamp: payload.timestamp || new Date().toISOString(),
          level: payload.level || 'info',
          agent: payload.agent || 'pipeline',
          message: payload.message || '',
        };
        const currentRecord = useAppStore.getState().currentRun;
        setCurrentRun(appendLogLine(currentRecord, entry));
      } catch {
        void refreshRun(runId);
      }
    });

    es.addEventListener('cluster.ready', (event) => {
      try {
        const payload = JSON.parse((event as MessageEvent).data || '{}');
        const currentRecord = useAppStore.getState().currentRun;
        if (!currentRecord || currentRecord.runId !== runId) return;
        setCurrentRun({ ...currentRecord, message: payload.message || currentRecord.message });
      } catch {
        void refreshRun(runId);
      }
    });

    es.addEventListener('task.completed', () => {
      syncTerminalState('completed');
      void refreshRun(runId);
    });

    es.addEventListener('task.failed', (event) => {
      try {
        const payload = JSON.parse((event as MessageEvent).data || '{}');
        const errors = Array.isArray(payload.errors) ? payload.errors : undefined;
        const message = typeof payload.error === 'string' ? payload.error : payload.message;
        syncTerminalState('failed', message, errors as any);
      } catch {
        syncTerminalState('failed');
      }
      void refreshRun(runId);
    });

    es.onerror = () => {
      if (closedRef.current) return;
      useAppStore.getState().setSseConnected(false);
      if (!pollingRef.current) {
        void refreshRun(runId);
        pollingRef.current = setInterval(() => {
          void refreshRun(runId);
        }, 15000);
        syncSharedRefs();
      }
    };
  }, [addToHistory, refreshRun, setCurrentRun, stopPolling, syncSharedRefs]);

  const cleanup = useCallback(() => {
    closedRef.current = true;
    subscribedRunIdRef.current = '';
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
    syncSharedRefs();
    stopPolling();
    useAppStore.getState().setSseConnected(false);
  }, [stopPolling, syncSharedRefs]);

  useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  return { primeRun, launchRun, subscribeSSE, refreshRun, cleanup, isLiveStatus };
}
